import { Component, TemplateRef, ViewChild, effect, inject } from '@angular/core';
import { take } from 'rxjs';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { PoliticasEntregaStore } from '../../../stores/politicas-entrega.store';
import { POLITICAS_ENTREGA_INCOTERMS, RegionPolitica } from '../../../models/politica-entrega.model';
import { PoliticaEntregaCellVm, PoliticaEntregaDetalleSaveValue } from '../../../models/politica-entrega.vm';
import { PoliticasEntregaUiEffectsService } from '../ui-effects/politicas-entrega-ui-effects.service';

@Component({
  selector: 'app-politicas-entrega-tabla',
  templateUrl: './politicas-entrega-tabla.component.html',
  styleUrls: ['./politicas-entrega-tabla.component.css'],
})
export class PoliticasEntregaTablaComponent {
  @ViewChild('detalleTpl', { static: true }) detalleTpl!: TemplateRef<unknown>;
  @ViewChild('cargaMasivaTpl', { static: true }) cargaMasivaTpl!: TemplateRef<unknown>;
  @ViewChild('confirmDeleteTpl', { static: true }) confirmDeleteTpl!: TemplateRef<unknown>;

  readonly store = inject(PoliticasEntregaStore);
  readonly modalService = inject(NgbModal);
  private readonly uiEffects = inject(PoliticasEntregaUiEffectsService);
  readonly incotermsItems = POLITICAS_ENTREGA_INCOTERMS;

  private detalleModalRef: NgbModalRef | null = null;
  private cargaMasivaModalRef: NgbModalRef | null = null;
  private confirmDeleteModalRef: NgbModalRef | null = null;
  selectedCell: PoliticaEntregaCellVm | null = null;

  /** Validación al pulsar Buscar sin región (mensaje bajo el select). */
  regionBuscarError = '';

  abrirDetalle(cell: PoliticaEntregaCellVm): void {
    this.selectedCell = cell;
    const ref = this.modalService.open(this.detalleTpl, {
      centered: true,
      backdrop: 'static',
    });
    this.detalleModalRef = ref;
    ref.hidden.pipe(take(1)).subscribe(() => {
      if (this.detalleModalRef === ref) {
        this.detalleModalRef = null;
      }
      this.selectedCell = null;
    });
  }

  abrirModalCargaMasiva(): void {
    const ref = this.modalService.open(this.cargaMasivaTpl, { centered: true });
    this.cargaMasivaModalRef = ref;
    ref.hidden.pipe(take(1)).subscribe(() => {
      if (this.cargaMasivaModalRef === ref) {
        this.cargaMasivaModalRef = null;
      }
    });
  }

  subirExcel(file: File): void {
    this.store.subirExcel(file).subscribe({
      next: (importResult) => {
        this.cargaMasivaModalRef?.dismiss();
        this.cargaMasivaModalRef = null;
        this.buscarPoliticasConFeedback(() => {
          this.uiEffects.showExcelImportResult(importResult);
        });
      },
      error: () => {
        this.uiEffects.showExcelError();
      },
    });
  }

  guardarDetalle(value: PoliticaEntregaDetalleSaveValue): void {
    const selectedCell = this.selectedCell;
    if (!selectedCell) {
      return;
    }

    this.detalleModalRef?.dismiss();
    this.detalleModalRef = null;
    this.store.guardarCelda({
      comuna: selectedCell.comuna,
      pallet: selectedCell.pallet,
      centro: value.centro,
      horas: value.horas,
    }).subscribe({
      next: () => {
        this.buscarPoliticasConFeedback(() => {
          this.uiEffects.showGuardarSuccess();
        });
      },
      error: () => {
        this.uiEffects.showGuardarError();
      },
    });
  }

  solicitarEliminarDetalle(): void {
    const id = this.selectedCell?.id ?? 0;
    if (id > 0) {
      this.confirmDeleteModalRef = this.modalService.open(this.confirmDeleteTpl, {
        centered: true,
        backdrop: 'static',
      });
    }
  }

  confirmarEliminarDetalle(): void {
    const id = this.selectedCell?.id ?? 0;
    this.confirmDeleteModalRef?.dismiss();
    this.confirmDeleteModalRef = null;

    if (id > 0 && !this.store.loading()) {
      this.detalleModalRef?.dismiss();
      this.detalleModalRef = null;
      this.store.eliminarCelda(id).subscribe({
        next: () => {
          this.buscarPoliticasConFeedback(() => {
            this.uiEffects.showEliminarSuccess();
          });
        },
        error: () => {
          this.uiEffects.showEliminarError();
        },
      });
    }
  }

  cancelarEliminarDetalle(): void {
    this.confirmDeleteModalRef?.dismiss();
    this.confirmDeleteModalRef = null;
  }

  cerrarDetalleModal(): void {
    this.detalleModalRef?.dismiss();
    this.detalleModalRef = null;
    this.selectedCell = null;
  }

  cerrarCargaMasivaModal(): void {
    this.cargaMasivaModalRef?.dismiss();
    this.cargaMasivaModalRef = null;
  }

  onRegionSeleccionadaChange(region: RegionPolitica | null): void {
    this.regionBuscarError = '';
    this.store.setRegionSeleccionada(region);
  }

  buscar(): void {
    this.regionBuscarError = '';
    this.buscarPoliticasConFeedback();
  }

  private buscarPoliticasConFeedback(onSuccess?: () => void): void {
    this.store.clearError();
    this.store.buscarPoliticas().subscribe({
      next: () => {
        onSuccess?.();
      },
      error: () => {
        this.uiEffects.showPoliticasLoadError();
      },
    });
  }
}
